import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Alert, Box, Button, Link, Spinner } from '@nimbus-ds/components';
import { useMutation } from '@tanstack/react-query';
import AppLayout from 'App/components/AppLayout';
import { ROUTES } from 'App/Routes/constants';
import { ErrorMessage, WarningModal } from 'commons/components';
import { useBankAccount, useIsMobile, useToast } from 'commons/hooks';
import { bankAccountService } from 'commons/services';
import { useTranslationWithPrefix } from 'commons/utils';
import { BankAccountValidator } from 'domains/BankAccountRegistration/BankAccountValidator/BankAccountValidator.interface';
import BankAccountInputs from './BankAccountInputs/BankAccountInputs';
import BankAccountRegistrationSkeleton from './BankAccountRegistrationSkeleton';
import ConfirmationModal from './ConfirmationModal';

interface BankAccountRegistrationProps {
  bankAccountValidator: BankAccountValidator;
}

const BankAccountRegistration = ({
  bankAccountValidator,
}: BankAccountRegistrationProps): JSX.Element => {
  const { t } = useTranslationWithPrefix('bankAccountRegistration');
  const { showToastSuccess, showToastError } = useToast();
  const history = useHistory();
  const isMobile = useIsMobile();
  const [changed, setChanged] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [bank, setBank] = useState('');
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [error, setError] = useState({
    beneficiaryName: '',
    accountNumber: '',
    bankName: '',
  });
  const bankAccount = useBankAccount();

  const updateBankAccountRegistration = useMutation({
    mutationFn: () => {
      return bankAccountService.patchBankAccount({
        holderName: beneficiaryName,
        bankName: bank,
        accountNumber: accountNumber,
      });
    },
    onSuccess: () => {
      bankAccount.refetch();
      showToastSuccess(t('success'));
      setConfirmationModalOpen(false);
      history.goBack();
    },
    onError: () => {
      setConfirmationModalOpen(false);
      showToastError(t('error'));
    },
  });

  useEffect(() => {
    setBeneficiaryName(bankAccount.data?.holderName ?? '');
    setAccountNumber(bankAccount.data?.accountNumber ?? '');
    setBank(bankAccount.data?.bankName ?? '');
  }, [bankAccount.data]);

  const validateAccountNumber = useCallback(
    (accountNumber: string) => {
      if (bankAccountValidator.isValid(accountNumber)) {
        const associatedBank =
          bankAccountValidator.getAssociatedBank(accountNumber);
        setBank(associatedBank);
        setError((prevError) => ({
          ...prevError,
          bankName: '',
        }));
      } else {
        setBank('');
      }
    },
    [bankAccountValidator, setBank, setError],
  );

  if (bankAccount.isLoading) {
    return <BankAccountRegistrationSkeleton />;
  }
  if (bankAccount.isError) {
    return (
      <AppLayout title={t('title')} subtitle={t('subtitle')}>
        <Box
          display="flex"
          alignItems="stretch"
          justifyContent="flex-end"
          flexDirection="column"
        >
          <ErrorMessage />
        </Box>
      </AppLayout>
    );
  }

  const bankAccountRegistered = bankAccount.data.isRegistered;
  const fiscalNumber = bankAccount.data.fiscalDocument;

  const handleChangeAccountNumber = (event: ChangeEvent<HTMLInputElement>) => {
    const accountNumber = event.target.value;
    setAccountNumber(accountNumber);

    setError((prevError) => ({
      ...prevError,
      accountNumber: '',
    }));

    validateAccountNumber(accountNumber);

    if (!changed) setChanged(true);
  };

  const handleChangeBeneficiaryName = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setBeneficiaryName(event.target.value);

    setError((prevError) => ({
      ...prevError,
      beneficiaryName: '',
    }));

    if (!changed) setChanged(true);
  };

  const redirectBack = () => {
    history.goBack();
  };

  const getAccountNumberError = () => {
    if (!accountNumber) return t('errors.noAccountNumber');
    if (!bankAccountValidator.isValid(accountNumber))
      return t('errors.invalidAccountNumber');
    if (bankAccountValidator.isValid(accountNumber) && !bank)
      return t('errors.blockedBank');
    return '';
  };

  const getNameError = () => {
    if (!beneficiaryName) return t('errors.noName');
    if (beneficiaryName.split(' ').length < 2) return t('errors.noLastName');
    return '';
  };

  const getBankNameError = () => {
    if (bank === '') return t('errors.noBankName');
    return '';
  };

  const verifyFields = () => {
    const accountNumberError = getAccountNumberError();
    const beneficiaryError = getNameError();
    const bankNameError = getBankNameError();
    if (!accountNumberError && !beneficiaryError && !bankNameError) {
      setConfirmationModalOpen(true);
    } else {
      setError({
        accountNumber: accountNumberError,
        beneficiaryName: beneficiaryError,
        bankName: bankNameError,
      });
    }
  };

  const dismissComfirmationModal = () => {
    setConfirmationModalOpen(false);
  };

  const submit = () => {
    if (!changed) {
      showToastSuccess(t('success'));
      redirectBack();
      return;
    }

    updateBankAccountRegistration.mutate();
  };

  const redirectOrWarningModal = () => {
    if (changed) {
      setWarningModalOpen(true);
    } else {
      redirectBack();
    }
  };

  const Buttons = (
    <Box gap="4" display="flex" flexDirection="row">
      <Button
        onClick={redirectOrWarningModal}
        disabled={updateBankAccountRegistration.isLoading}
      >
        {t('cancel')}
      </Button>
      <Button
        appearance="primary"
        onClick={verifyFields}
        disabled={updateBankAccountRegistration.isLoading}
      >
        {updateBankAccountRegistration.isLoading && <Spinner size="small" />}
        {t('save')}
      </Button>
    </Box>
  );

  const MobileSaveLink = (
    <Link
      appearance="neutral"
      onClick={verifyFields}
      disabled={updateBankAccountRegistration.isLoading}
      as="button"
      textDecoration="none"
    >
      {updateBankAccountRegistration.isLoading && <Spinner size="small" />}
      {t('save')}
    </Link>
  );

  return (
    <AppLayout
      title={t('title')}
      footer={Buttons}
      navBarItems={isMobile ? MobileSaveLink : undefined}
      backLinkPath={ROUTES.CONFIG}
    >
      <Box display="flex" flexDirection="column" gap="4">
        <Alert show appearance="neutral">
          {t('documentMustMatch')}
        </Alert>
        <BankAccountInputs
          isRegistered={bankAccountRegistered}
          fiscalNumber={bankAccountValidator.formatFiscalNumber(fiscalNumber)}
          bank={bank}
          beneficiaryName={beneficiaryName}
          accountNumber={accountNumber}
          handleChangeAccountNumber={handleChangeAccountNumber}
          handleChangeBeneficiaryName={handleChangeBeneficiaryName}
          error={error}
        />
      </Box>
      <ConfirmationModal
        bankAccountInfo={{
          accountNumber: accountNumber,
          bank: bank,
          beneficiary: beneficiaryName,
          fiscalNumber: fiscalNumber,
        }}
        buttonDisabled={updateBankAccountRegistration.isLoading}
        open={confirmationModalOpen}
        onDismiss={dismissComfirmationModal}
        onConfirm={submit}
      />
      <WarningModal
        exitButtonDisabled={updateBankAccountRegistration.isLoading}
        onDismiss={() => setWarningModalOpen(false)}
        onExit={redirectBack}
        open={warningModalOpen}
      />
    </AppLayout>
  );
};

BankAccountRegistration.Skeleton = BankAccountRegistrationSkeleton;
export default BankAccountRegistration;
